import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import styled, { ThemeProvider } from 'styled-components';
import {
  accent,
  brand,
  rgba,
  radius,
  semantic,
  shadow,
  space,
  typeRole,
  weight,
  zIndex,
} from '@repo/design-tokens';
import {
  Banner,
  Button,
  Card,
  CHANGE_GLYPH,
  DockedCard,
  DockedCardFloating,
  DockedCardHead,
  Dot,
  HoverCard,
  Icon,
  Kbd,
  LIST_PANEL_FOLDED_WIDTH,
  Skeleton,
  ToastRegion,
} from '@repo/design-system';
import type { IconName, TagTone } from '@repo/design-system';
import {
  CameraWallTile,
  KindGlyph,
  type CameraWallFeed,
  type FrameRegion,
  visuallyHidden,
} from '@repo/ui';
import {
  FloorTabs,
  PLAN_SELECTION_COLOUR,
  PlanLegend,
  PlanLegendBar,
  SMALL_GLASS_PADDING,
  smallGlassCss,
  ReadOnlyCanvas,
  ReadOnlyCanvasRendererProvider,
  plannerTheme,
  type CanvasMarker,
} from '@repo/ui/planner';
import type { FloorPlannerLayout } from '@repo/frontend-types';

import {
  FEEDS_LOADING,
  FEEDS_UNREADABLE_BODY,
  FEEDS_UNREADABLE_TITLE,
  machineColoursOf,
  markersOnModels,
  NO_FEEDS_BODY,
  SHORTLIST_SIZE,
} from './observerStageModel';
import { NAME_COLLATOR } from './inventorySort';
import { suggestionCaption, STATUS_DOT } from './observerStageModel';
import type { StageView } from './observerStageModel';
import { OBSERVER_STAGE_ATTRIBUTE } from '../../components/PolygonPlotter2/PolygonPlotter2';
import type { FeedScore } from './observerSourceRanking';
import { cornerCoverage, coverageMarker } from './cornerCoverage';
import type { ObserverTargetKind } from './observerTypes';
import { createZoomReadout, ZoomPercent } from './observerWorkspace/zoomReadout';
import { PlanPictureRendererContext } from '../../components/3D/planView/planPictureRenderer';
import {
  GLASS_EDGE,
  GLASS_GAP,
  GLASS_START,
  INSPECTOR_WIDTH,
  LIST_PANEL_WIDTH,
  PANEL_TOP,
} from './glassLayout';

/**
 * The Observers step's stage (`observers-page-stage`): the large card to the right of the list that
 * shows the plan, the cameras likely to see a picked entity, every camera, or the entity's cameras
 * side by side (FR17–FR28).
 *
 * Presentational. It owns no observer state — the step does, and passes what to show and what to
 * call — so the next feature (`camera-stage`) can render one camera into the same card. Built only
 * from design-system and `@repo/ui` parts; every value is a token.
 */

/** Where the stage's Undo toast appears: top-centre of the stage, never the viewport's corner. */
export const STAGE_TOAST_REGION = 'observers-stage';

/** The *Plan* buttons' name: the header's on the camera view, and the corner plan's elsewhere. */
export const PLAN_BACK_LABEL = 'Put the plan back on stage';
/**
 * The corner plan's *Plan* on the camera view, where the header carries a *Plan* of its own: a
 * distinct name, which also names the corner plan's region (Block A3).
 */
export const CORNER_PLAN_LABEL = 'Plan, corner preview';

/**
 * The corner plan's fit margin, on every view (`observer-corner-plan-camera-framing`, R6): the
 * read-only plans' 60 px leaves the 220 × 146 card 26 px of height, so every fit opened at the
 * minimum zoom. Layout geometry, not a brand value.
 */
export const CORNER_FIT_PADDING_PX = 12;

/** The picked entity, as the header names it. */
export interface StageEntity {
  refid: string;
  label: string;
  code?: string;
  kind: ObserverTargetKind;
  /** Complete observers, and the cameras they are on — the side-by-side sub-line. */
  observerCount: number;
  cameraCount: number;
}

export interface StageFloor {
  index: number;
  name: string;
  /** Entities needing attention on this floor; the pill is hidden at 0. */
  attention: number;
  /**
   * How many of them need checking (`onboarding-status-colours-observers`, R7). Not drawn today: the
   * floating floor selector shows one count tone (`onboarding-persistent-scene-observers-glass`,
   * plan decision 3, on Rob's picture-review list), so this is kept for that review. Absent counts
   * as none.
   */
  checking?: number;
}

/** What the plan's hover card says about an entity. */
export interface StageHoverText {
  title: string;
  code?: string;
  status?: { label: string; tone: TagTone };
}

/** The entity under the plan's pointer, and its centre in the plan pane's pixels. */
export interface StageHoverPoint {
  refid: string;
  at: { x: number; y: number };
}

/**
 * Where the plan's hover lives: a tiny store the stage writes and only its readers subscribe to —
 * the stage itself and the list's row glow. Held as state in the step, every hover re-rendered the
 * whole step (measured: 37 % of frames over 20 ms while hovering).
 */
export interface HoverStore {
  get: () => StageHoverPoint | null;
  set: (next: StageHoverPoint | null) => void;
  subscribe: (listener: () => void) => () => void;
}
export const createHoverStore = (): HoverStore => {
  let current: StageHoverPoint | null = null;
  /*
   * Coalesced to one update a frame: crossing from one entity to the next fires a leave and an enter
   * in the same frame, and only where the pointer ENDS UP is worth a render.
   */
  let pending: StageHoverPoint | null = null;
  let frame: number | null = null;
  const listeners = new Set<() => void>();
  const flush = () => {
    frame = null;
    if (current?.refid === pending?.refid) return;
    current = pending;
    listeners.forEach(l => l());
  };
  return {
    get: () => current,
    set: next => {
      pending = next;
      if (frame === null) frame = requestAnimationFrame(flush);
    },
    subscribe: listener => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
};
export const useHovered = (store: HoverStore): StageHoverPoint | null =>
  useSyncExternalStore(store.subscribe, store.get);

interface Props {
  className?: string;
  view: StageView;
  entity: StageEntity | null;

  // ── The plan ──────────────────────────────────────────────────────────────
  layout: FloorPlannerLayout | null;
  floorIndex: number;
  floors: readonly StageFloor[];
  /**
   * The status marks, without emphasis. The stage draws the picked one in the plan's selection colour
   * (`pickedDot`); the hovered one gets a dashed ring in that colour over the canvas.
   */
  markers: readonly CanvasMarker[];
  selectedId: string | null;
  onPickFromPlan: (refid: string, kind: 'surface' | 'item') => void;
  hoverStore: HoverStore;
  /** The hover card's words for an entity; `null` draws no card. */
  describeHover: (refid: string) => StageHoverText | null;
  recentreSignal: number;
  onRecentre: () => void;
  /** How the observer read went — the legend says when the dots cannot be trusted. */
  observersStatus: 'pending' | 'ready' | 'failed';
  /** The running-model read: when it failed, *needs checking* cannot be decided. */
  modelFailed: boolean;

  // ── The camera grids ──────────────────────────────────────────────────────
  feeds: readonly CameraWallFeed[];
  streamsStatus: 'loading' | 'ready' | 'failed';
  /** The picked entity's cameras in likelihood order, empty when nothing is placed to judge by. */
  scores: readonly FeedScore[];
  onOpenFeed: (endpointId: number) => void;
  onRefreshFeed: (id: CameraWallFeed['id']) => void;
  zoomRegionsByFeed?: ReadonlyMap<number, readonly FrameRegion[]>;
  onShowAllCameras: () => void;
  onShowSuggestions: () => void;
  onAddCamera: () => void;
  /** The side-by-side view, rendered by the step (it owns the regions). */
  sideBySide: React.ReactNode;

  /** The corner plan, P, and the plan's own Esc: back to the plan. */
  onPlanBack: () => void;
  /** To the step where feeds are added: offered only when the read succeeded with none. */
  onGoToCameraFeeds?: () => void;

  // ── One camera (`observers-page-camera-stage`) ─────────────────────────────
  /** The camera view's body, rendered by the step (it owns the drawing area). */
  camera?: React.ReactNode;
  /** The header's facts while one camera is on stage. */
  cameraHeader?: CameraHeader | null;
  /**
   * The step's newest sentence (an undo, a link, a refresh's answer, the heat read's outcome), said
   * through this stage's one polite region after its title (Block A4).
   */
  note?: string;
  /** The corner plan picks: an entity clicked there is the next pick (FR13). */
  onPickFromCorner?: (refid: string, kind: 'surface' | 'item') => void;

  // ── Under the glass (`onboarding-persistent-scene-observers-glass`) ────────
  /** The list panel is open: the floating stage card starts right of it, else of the folded bar. */
  listOpen?: boolean;
  /** Show map (R5.3): the street map under the plan. The step's view state, never stored. */
  showMap?: boolean;
  onShowMapChange?: (show: boolean) => void;
  /** Draw Observer (R3.6): open this camera with one region armed for the picked item. */
  onDrawObserver?: (endpointId: number) => void;
  /** The likely cameras' close (R3.7): one step back, as Esc. */
  onClosePanel?: () => void;
}

/** What the stage header shows for one camera (FR2). */
export interface CameraHeader {
  name: string;
  /** The picture's capture time, `HH:MM`; `null` when there is no picture. */
  pictureTime: string | null;
}

// ── Layout ────────────────────────────────────────────────────────────────────

/**
 * The stage (`onboarding-persistent-scene-observers-glass`): the whole step area. The plan fills it,
 * and the right-hand panel, the floating stage card, the floors, legend, dock and toasts float over
 * it, each at its offset from `glassLayout.ts`.
 */
const StageRoot = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  /* The design-system Modal's root carries no z-index, so the camera tiles' chrome (z 1-4) would
     paint above a dialog opened over them: the stage is its own stacking context. */
  isolation: isolate;

  /* Focus parks here for a frame while a camera opens, on its way to the picture (see
     STAGE_ROOT_ATTRIBUTE); it draws no ring for that. */
  &:focus {
    outline: none;
  }
`;

/**
 * Marks the stage's root. When the likely cameras panel closes because a camera opened, the panel
 * returns focus here rather than to the list row that opened it, and the step reads focus on this
 * element as unplaced, so its picture takes focus as it did before the panel (FR23).
 */
export const STAGE_ROOT_ATTRIBUTE = 'data-observer-stage-root';

/** The plan, edge to edge under the glass (R1.1). */
const PlanLayer = styled.div`
  position: absolute;
  inset: 0;
`;

/**
 * The camera view and side by side: today's stage card, floating over the plan from right of the
 * list panel to the window's right edge, 12 px below the bar and 8 px above the bottom (R3.9).
 */
const Shell = styled(Card)`
  position: absolute;
  top: ${PANEL_TOP};
  right: ${GLASS_EDGE};
  bottom: ${GLASS_EDGE};
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 0;
  overflow: hidden;
`;

const Body = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const CanvasHost = styled.div`
  position: absolute;
  inset: 0;
`;

/**
 * The hover card's layer: pinned over the plan, never catching the pointer. Portalled out of the
 * stage, whose rounded card clips, so a card hanging 220 px left of a dot near the stage's edge
 * shows whole over the navigator (FR20).
 */
const HoverLayer = styled.div`
  position: fixed;
  z-index: ${zIndex.tooltip};
  pointer-events: none;
`;

/* The floating floor selector's span (R4.1): between the list panel and the right edge (or the
   right-hand panel), the selector centred in it. Pointer-transparent; the selector takes its
   pointers back. */
const FloorSlot = styled.div`
  position: absolute;
  top: ${PANEL_TOP};
  display: flex;
  justify-content: center;
  pointer-events: none;
  z-index: 1;
`;

/** A legend entry's dot: the state's plan dot (each listed state has one). */
const legendDot = (state: 'unobserved' | 'check' | 'added-unobserved'): string =>
  STATUS_DOT[state] as string;

/** A floor's attention count, as the floor selector says it (R4.2, Block A3). */
const attentionCountLabel = (count: number) => `${count} need attention`;

/**
 * The legend bar and the dock, one row at the bottom left (R5): the legend 12 px right of the list
 * panel, the dock 12 px right of the legend. The legend wraps (Show map takes a second line) before
 * it would pass under the dock (R5.4). Pointer-transparent but for the controls.
 */
const LegendRow = styled.div`
  position: absolute;
  bottom: ${GLASS_EDGE};
  display: flex;
  align-items: flex-end;
  gap: ${GLASS_GAP};
  pointer-events: none;
  z-index: 1;
`;

/** The legend bar sits in the row's flow rather than at its own corner. */
const LEGEND_IN_ROW: React.CSSProperties = {
  position: 'relative',
  left: 'auto',
  bottom: 'auto',
  maxWidth: 'none',
  minWidth: 0,
  flex: '0 1 auto',
};

/** Recentre and the zoom percentage, as small glass (R5.4). */
const Dock = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  gap: ${space[2]};
  padding: ${SMALL_GLASS_PADDING} ${space[2]};
  ${smallGlassCss}
  pointer-events: auto;

  .divider {
    width: 1px;
    align-self: stretch;
    background: ${semantic.border.faint};
  }
`;

/**
 * The likely cameras' panel (R3.1): 12 px below the bar, 8 px from the right, 360 px wide, as tall as
 * its content up to 8 px above the bottom; the card's body scrolls past that.
 */
const PanelSlot = styled.div`
  position: absolute;
  top: ${PANEL_TOP};
  right: ${GLASS_EDGE};
  max-height: calc(100% - ${PANEL_TOP} - ${GLASS_EDGE});
  display: flex;
  flex-direction: column;
  z-index: 2;
`;

const PanelCaption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space[2]};

  .caption {
    ${typeRole.caption1};
    font-weight: ${weight.semibold};
    color: ${semantic.text.secondary};
  }
`;

const PanelHint = styled.p`
  display: flex;
  align-items: center;
  gap: ${space[2]};
  margin: 0;
  font-size: ${typeRole.caption1.fontSize};
  color: ${semantic.text.secondary};

  .bulb {
    color: ${brand.cyan};
  }
`;

const PanelFoot = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
`;

const GridScroll = styled.div`
  position: absolute;
  inset: 0;
  overflow-y: auto;
  /* Room at the foot for the hint and the corner plan, which sit over the grid's last row. */
  padding: ${space[4]} ${space[4]} 184px;
`;

/* The likely cameras in the 360 px panel: two to a row, so the four sit as a 2 × 2 (frame 03). */
const Grid = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${space[2]};
`;

const TileItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: ${space[1]};
  min-width: 0;

  .caption {
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    font-weight: ${weight.medium};
    color: ${semantic.text.secondary};
  }
`;

const Note = styled.p`
  margin: 0 0 ${space[3]};
  font-size: ${typeRole.body1.fontSize};
  line-height: ${typeRole.body1.lineHeight};
  color: ${semantic.text.secondary};
`;

/** The corner's canvas under the picture renderer when the switch names one; as it is otherwise. */
const CornerRenderer = ({
  renderer,
  children,
}: {
  renderer: React.ComponentProps<typeof ReadOnlyCanvasRendererProvider>['renderer'] | null;
  children: React.ReactNode;
}) =>
  renderer ? (
    <ReadOnlyCanvasRendererProvider renderer={renderer}>{children}</ReadOnlyCanvasRendererProvider>
  ) : (
    <>{children}</>
  );

/** The corner plan: a picture of the plan, and one named control that puts it back. */
const CornerPlan = styled.div`
  position: absolute;
  right: ${space[4]};
  bottom: ${space[4]};
  width: 220px;
  height: 146px;
  /* On the camera view it fills its movable slot, which carries the size the person resized it to
     (\`observers-corner-plan-resize\`). */
  &.on-camera {
    position: relative;
    right: auto;
    bottom: auto;
    width: 100%;
    height: 100%;
    pointer-events: auto;
  }
  border-radius: ${radius.lg};
  overflow: hidden;
  box-shadow: ${shadow.glassSm};
  background: ${semantic.surface.base};

  .back {
    position: absolute;
    left: ${space[2]};
    bottom: ${space[2]};
  }
`;

/** The stage's live region: always in the DOM, written after mount so each change is heard. */
const StatusText = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

/* The design system's own empty card (dashed, centred) made interactive — not a restyled Button. */
const AddTile = styled(Card)`
  min-height: 160px;
  gap: ${space[1]};
  text-align: center;

  .add-title {
    font-size: ${typeRole.body1.fontSize};
    font-weight: ${weight.semibold};
    color: ${semantic.text.primary};
  }
  .add-sub {
    font-size: ${typeRole.caption1.fontSize};
    color: ${semantic.text.secondary};
  }
`;

/**
 * A picked entity's mark, in the 3D plan's selection colour (`onboarding-status-colours-observers`,
 * R9): a space drawn as its status outline is that outline in the selection colour, emphasised; a
 * machine is the selection ring alone, since its model carries its status
 * (`spec-onboarding-persistent-scene-status-colour`, R4, superseding Rob's 2026-10-06 status dot
 * inside the ring); an entity with no status is the selection ring alone too, or its outline when
 * the caller knows it is a space (`asOutline`). The added disc stays.
 */
export const pickedDot = (
  markers: readonly CanvasMarker[],
  refid: string,
  asOutline = false,
): CanvasMarker => {
  const status = markers.find(m => m.id === refid);
  const change = status?.change ? { change: status.change } : {};
  if (status?.shape === 'outline' || (!status && asOutline)) {
    return {
      id: refid,
      color: PLAN_SELECTION_COLOUR,
      shape: 'outline',
      emphasised: true,
      ...change,
    };
  }
  return {
    id: refid,
    color: PLAN_SELECTION_COLOUR,
    emphasised: true,
    statusOnModel: true,
    ...change,
  };
};

/** The hover ring's outer size, screen px: clear of the picked machine's ring inside it. */
const HOVER_RING_SIZE = 22;

/**
 * The hovered entity's mark over the canvas: the plan's hover look, a 2px dashed ring in the 3D plan's
 * selection colour around the entity (a machine's status is its model's colour), which it never covers (R9). Drawn here, not in the plan's
 * markers, so a hover never re-renders the plan.
 */

const HoverRing = styled.span`
  position: absolute;
  box-sizing: border-box;
  width: ${HOVER_RING_SIZE}px;
  height: ${HOVER_RING_SIZE}px;
  margin: ${-HOVER_RING_SIZE / 2}px 0 0 ${-HOVER_RING_SIZE / 2}px;
  border-radius: ${radius.pill};
  border: 2px dashed ${PLAN_SELECTION_COLOUR};
  pointer-events: none;
`;

/** The legend's picked-or-hovered swatch: the same dashed ring, at the legend's dot size. */
const LegendRing = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  &::before {
    content: '';
    box-sizing: border-box;
    width: 12px;
    height: 12px;
    border-radius: ${radius.pill};
    border: 2px dashed ${PLAN_SELECTION_COLOUR};
  }
`;

/** The legend's added entry: the work-to-do dot with the lasting added glyph beside it (R4). */
const LegendAdded = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${space[1]};
  .added-glyph {
    color: ${semantic.status.lastingMark.ink};
  }
`;

/**
 * The plan's hover — the ring and the card — and the only part of the stage subscribed to it, so
 * crossing the plan re-renders this and nothing else (measured: the stage and the canvas re-rendering
 * on every hover put 34 % of frames over 20 ms).
 */
const HoverOverlay: React.FC<{
  store: HoverStore;
  describe: React.MutableRefObject<(refid: string) => StageHoverText | null>;
  /** The plan's host: the layer sits over it, so the canvas's pane pixels land on the plan. */
  host: React.RefObject<HTMLDivElement | null>;
}> = ({ store, describe, host }) => {
  const hovered = useHovered(store);
  const text = hovered ? describe.current(hovered.refid) : null;
  const box = hovered ? host.current?.getBoundingClientRect() : null;
  if (!hovered || !box) return null;
  return createPortal(
    <HoverLayer style={{ left: box.left, top: box.top, width: box.width, height: box.height }}>
      <HoverRing style={{ left: hovered.at.x, top: hovered.at.y }} aria-hidden="true" />
      {text && (
        <HoverCard
          open
          anchor={hovered.at}
          title={text.title}
          code={text.code}
          status={text.status}
          hint={{ label: 'Click to set up' }}
        />
      )}
    </HoverLayer>,
    document.body,
  );
};

/** The corner plan's hover card: the entity under the pointer, and *Click to pick* (FR13). */
const CornerHover: React.FC<{
  hover: { refid: string; at: { x: number; y: number } };
  describe: React.MutableRefObject<(refid: string) => StageHoverText | null>;
  host: React.RefObject<HTMLDivElement | null>;
}> = ({ hover, describe, host }) => {
  const text = describe.current(hover.refid);
  const box = host.current?.getBoundingClientRect();
  if (!text || !box) return null;
  // The anchor is the entity's centre, which a zoomed-in corner plan can leave outside its box
  // (`observer-corner-plan-zoom-pan`, review): hold the card to the corner it was hovered in.
  const anchor = {
    x: Math.min(Math.max(hover.at.x, 0), box.width),
    y: Math.min(Math.max(hover.at.y, 0), box.height),
  };
  return createPortal(
    <HoverLayer style={{ left: box.left, top: box.top, width: box.width, height: box.height }}>
      <HoverCard
        open
        anchor={anchor}
        title={text.title}
        code={text.code}
        status={text.status}
        hint={{ label: 'Click to pick' }}
      />
    </HoverLayer>,
    document.body,
  );
};

/** The camera view fills the stage's body below the header. */
const CameraBody = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
`;

// ── Pieces ────────────────────────────────────────────────────────────────────

/** What the header says in each view (FR19). */
const headerFor = (
  view: StageView,
  entity: StageEntity | null,
  floorName: string,
  camera?: CameraHeader | null,
): { icon: IconName; title: string; sub: string } => {
  if (view.view === 'camera') {
    return {
      icon: 'draw-polygon',
      title: camera?.name ?? 'Camera',
      // There is no mode to say (`observer-unified-draw-edit`): entering a camera says its name.
      sub: '',
    };
  }
  if (view.view === 'plan' || !entity) {
    return {
      icon: 'map',
      title: floorName,
      sub: 'Click an entity on the plan, or pick one from the list',
    };
  }
  if (view.view === 'side-by-side') {
    const kind =
      entity.kind === 'floor' ? 'Floor' : entity.kind === 'surface' ? 'Zone' : 'Equipment';
    const obs = `${entity.observerCount} observer${entity.observerCount === 1 ? '' : 's'}`;
    const cams = `${entity.cameraCount} camera${entity.cameraCount === 1 ? '' : 's'}`;
    return {
      icon: 'table-cells-large',
      title: entity.label,
      sub: [kind, entity.code, `${obs} on ${cams}`].filter(Boolean).join(' · '),
    };
  }
  if (view.view === 'all-cameras') {
    return {
      icon: 'video',
      title: `All cameras for ${entity.label}`,
      sub: view.linking
        ? 'Pick the camera this region was drawn on'
        : view.adding
          ? `Pick a camera to add for ${entity.label}`
          : 'Every camera, by name',
    };
  }
  return {
    icon: 'video',
    title: `Cameras likely to see ${entity.label}`,
    sub: view.linking
      ? 'Pick the camera this region was drawn on'
      : view.adding
        ? `Pick a camera to add for ${entity.label}`
        : 'In view on the plan first, then nearest',
  };
};

/** One camera tile in a grid, its caption beneath. Memoised: the wall patches a feed per frame. */
const GridTile = React.memo(function GridTile({
  feed,
  caption,
  best,
  onPick,
  onRefresh,
  zoomRegions,
}: {
  feed: CameraWallFeed;
  caption: string | null;
  best: boolean;
  onPick: (id: CameraWallFeed['id']) => void;
  onRefresh: (id: CameraWallFeed['id']) => void;
  zoomRegions?: readonly FrameRegion[];
}) {
  return (
    <TileItem data-endpoint={feed.id}>
      <CameraWallTile
        feed={feed}
        selected={best}
        onSelect={onPick}
        selectLabel={`Open ${feed.name}`}
        onRefresh={onRefresh}
        zoomRegions={zoomRegions}
      />
      {caption && <span className="caption">{caption}</span>}
    </TileItem>
  );
});

// ── The stage ────────────────────────────────────────────────────────────────

const ObserverStage: React.FC<Props> = ({
  className,
  view,
  entity,
  layout,
  floorIndex,
  floors,
  markers,
  selectedId,
  onPickFromPlan,
  hoverStore,
  describeHover,
  recentreSignal,
  onRecentre,
  observersStatus,
  modelFailed,
  feeds,
  streamsStatus,
  scores,
  onOpenFeed,
  onRefreshFeed,
  zoomRegionsByFeed,
  onShowAllCameras,
  onShowSuggestions,
  onAddCamera,
  sideBySide,
  onPlanBack,
  onGoToCameraFeeds,
  camera,
  cameraHeader,
  note,
  onPickFromCorner,
  listOpen = true,
  showMap = true,
  onShowMapChange = () => undefined,
  onDrawObserver,
  onClosePanel,
}) => {
  const floorName = floors.find(f => f.index === floorIndex)?.name ?? 'Floor plan';
  // Under the renderer switch the corner draws a picture of the 3D plan, not a live map of its own
  // (`spec-onboarding-3d-plan-corner-plan-snapshot`); null with the switch off.
  const cornerRenderer = useContext(PlanPictureRendererContext);
  const onCamera = view.view === 'camera' && !!cameraHeader;
  const header = headerFor(view, entity, floorName, onCamera ? cameraHeader : null);
  /*
   * The camera on stage's coverage on the floor the corner draws (`observer-corner-plan-camera-framing`,
   * R1 to R3): the corner fits it and outlines it. `null` off the camera view, and for a camera that
   * is not placed on this floor, which fits the whole floor.
   */
  const cameraOnStage = onCamera && view.view === 'camera' ? view.endpointId : null;
  const coverage = useMemo(
    () => (cameraOnStage === null ? null : cornerCoverage(layout, cameraOnStage, floorIndex)),
    [layout, cameraOnStage, floorIndex],
  );
  const [zoomStore] = useState(() => createZoomReadout());

  /*
   * `spec-onboarding-persistent-scene-status-colour`, R1 to R4: each machine's model is drawn in its
   * status colour, read from the status markers before any is drawn picked, so the picked machine
   * keeps its status on its model (*Observer still correct* still visibly changes the plan). Its
   * marker, and every machine's, then draws no dot.
   */
  const machineColours = useMemo(() => machineColoursOf(markers), [markers]);
  const modelMarkers = useMemo(() => markersOnModels(markers), [markers]);
  /*
   * The PICKED entity is drawn larger, in the plan's selection colour (`pickedDot`, R9): a machine is
   * the selection ring, its model in its status colour; a space's outline turns the selection colour.
   * The HOVERED one gets a dashed ring drawn over the canvas by {@link HoverOverlay}, so a hover
   * never re-renders the plan.
   */
  const shownMarkers = useMemo<CanvasMarker[]>(
    () =>
      selectedId
        ? [
            ...modelMarkers.filter(m => m.id !== selectedId),
            pickedDot(
              markers,
              selectedId,
              entity?.refid === selectedId && entity.kind === 'surface',
            ),
          ]
        : [...modelMarkers],
    [entity, markers, modelMarkers, selectedId],
  );
  /*
   * The corner plan draws every entity's status, so the next one to set up can be seen and picked
   * from it (FR13), and the picked entity as Rob's v3 draws it on a camera: a zone as its own butter
   * outline (ruling R4), keeping its added disc; otherwise as on the stage plan (`pickedDot`).
   */
  const cornerMarkers = useMemo<CanvasMarker[]>(() => {
    // The camera's coverage first, so every other mark draws over it (R2).
    const under = coverage ? [coverageMarker(coverage)] : [];
    const rest = [
      ...under,
      ...(entity ? modelMarkers.filter(m => m.id !== entity.refid) : modelMarkers),
    ];
    if (!entity) return rest;
    const picked: CanvasMarker =
      onCamera && entity.kind === 'surface'
        ? {
            id: entity.refid,
            color: accent.butter,
            fill: rgba(accent.butter, 0.22),
            shape: 'outline',
            emphasised: true,
            ...(markers.find(m => m.id === entity.refid)?.change
              ? { change: 'added' as const }
              : {}),
          }
        : pickedDot(markers, entity.refid, entity.kind === 'surface');
    return [...rest, picked];
  }, [coverage, entity, markers, modelMarkers, onCamera]);
  const cornerHostRef = useRef<HTMLDivElement>(null);
  const [cornerHover, setCornerHover] = useState<{
    refid: string;
    at: { x: number; y: number };
  } | null>(null);
  const onCornerHover = useCallback(
    (refid: string | null, at: { x: number; y: number } | null) =>
      setCornerHover(refid && at ? { refid, at } : null),
    [],
  );
  const pickFromCorner = useCallback(
    (refid: string, kind: 'surface' | 'item') => {
      setCornerHover(null);
      onPickFromCorner?.(refid, kind);
    },
    [onPickFromCorner],
  );
  const describeRef = useRef(describeHover);
  describeRef.current = describeHover;
  const canvasHostRef = useRef<HTMLDivElement>(null);
  // The plan's name says the floor and, once the observer read has answered, what needs attention.
  const floorAttention = floors.find(f => f.index === floorIndex)?.attention ?? 0;
  const planLabel =
    observersStatus === 'ready'
      ? `Floor plan of ${floorName}, ${floorAttention} ${floorAttention === 1 ? 'entity needs' : 'entities need'} attention`
      : `Floor plan of ${floorName}`;

  /* Stable callbacks into the memoised canvas: a zoom or a hover must not redraw the plan. */
  const pickRef = useRef(onPickFromPlan);
  pickRef.current = onPickFromPlan;
  const pickFromPlan = useCallback(
    (refid: string, kind: 'surface' | 'item') => pickRef.current(refid, kind),
    [],
  );
  /*
   * No hover while the view is zooming: the pointer stays put and the plan slides under it, so every
   * few frames a different entity would be "hovered" and its card flicker through (and, measured, a
   * frame dropped each time). A quarter-second after the last zoom step, hover answers again.
   */
  const lastZoomAtRef = useRef(0);
  const onZoomChange = useCallback(
    (percent: number) => {
      lastZoomAtRef.current = performance.now();
      hoverStore.set(null);
      zoomStore.set(percent);
    },
    [hoverStore, zoomStore],
  );
  const onHoverEntity = useCallback(
    (refid: string | null, at: { x: number; y: number } | null) => {
      if (performance.now() - lastZoomAtRef.current < 250) return hoverStore.set(null);
      hoverStore.set(refid && at ? { refid, at } : null);
    },
    [hoverStore],
  );
  // Leaving the plan view leaves nothing hovered.
  useEffect(() => {
    if (view.view !== 'plan') hoverStore.set(null);
  }, [view.view, hoverStore]);
  const planCanvas = useMemo(
    () =>
      layout ? (
        <ThemeProvider theme={plannerTheme}>
          <ReadOnlyCanvas
            layout={layout}
            floorIndex={floorIndex}
            driverName="observers-stage"
            basemapHidden={!showMap}
            selectedId={selectedId}
            onSelectEntity={pickFromPlan}
            markers={shownMarkers}
            machineColours={machineColours}
            onHoverEntity={onHoverEntity}
            recentreSignal={recentreSignal}
            onZoomChange={onZoomChange}
            ariaRole="img"
            ariaLabel={planLabel}
          />
        </ThemeProvider>
      ) : null,
    [
      planLabel,
      layout,
      floorIndex,
      selectedId,
      pickFromPlan,
      shownMarkers,
      machineColours,
      onHoverEntity,
      recentreSignal,
      onZoomChange,
      showMap,
    ],
  );

  /*
   * The polite region says the header's title on every change of view (Block A4). Written AFTER the
   * region mounts: a live region inserted with its text already in it is not reliably announced.
   */
  const [announced, setAnnounced] = useState('');
  /*
   * The likely cameras panel opening says how many it shows (`onboarding-persistent-scene-
   * observers-glass`, R3): *{k} cameras likely to see {item}*, once the feeds are read and a camera
   * ranks; otherwise the header's title, as every other view.
   */
  const shortlisted =
    view.view === 'suggestions' && streamsStatus === 'ready' && entity
      ? scores.slice(0, SHORTLIST_SIZE).filter(score => feeds.some(f => Number(f.id) === score.id))
          .length
      : 0;
  const spoken =
    shortlisted > 0 && entity
      ? `${shortlisted} camera${shortlisted === 1 ? '' : 's'} likely to see ${entity.label}`
      : header.title;
  useEffect(() => {
    setAnnounced(spoken);
  }, [spoken]);
  // After the title's effect, so a sentence given with a change of view is the one left to hear.
  useEffect(() => {
    if (note) setAnnounced(note);
  }, [note]);

  /*
   * A stable pick handler for the tiles, through a ref refreshed after every commit, so a frame
   * arriving re-renders one tile, not the grid.
   */
  const latest = useRef({ onOpenFeed });
  useEffect(() => {
    latest.current = { onOpenFeed };
  });
  const pick = useCallback((id: CameraWallFeed['id']) => {
    const n = Number(id);
    if (Number.isFinite(n)) latest.current.onOpenFeed(n);
  }, []);

  /** The plan stays drawn on the plan view and under the likely-cameras panel (R3.1). */
  const panelView = view.view === 'suggestions' || view.view === 'all-cameras';
  const showsPlan = view.view === 'plan' || panelView;
  /** The camera view and side by side: today's stage card, floating over the plan (R3.9). */
  const onCard = view.view === 'camera' || view.view === 'side-by-side';
  const legendLead =
    observersStatus === 'pending'
      ? 'Checking observers…'
      : observersStatus === 'failed'
        ? 'Observer status unavailable'
        : null;

  /*
   * Where the floating pieces sit (canvas frame 03; every offset from `glassLayout.ts`): right of the
   * list panel at its OPEN width whether or not it is folded, so nothing moves on fold (R7.1), and
   * left of the right-hand panel while it is open (R3.8, R4.1).
   */
  const besideList = `calc(${GLASS_START} + ${LIST_PANEL_WIDTH} + ${GLASS_GAP})`;
  const besidePanel = panelView
    ? `calc(${INSPECTOR_WIDTH} + ${GLASS_EDGE} + ${GLASS_GAP})`
    : GLASS_EDGE;

  /* The legend bar's height, which the toast region clears on the plan views (R6.3). */
  const legendRowRef = useRef<HTMLDivElement | null>(null);
  const [legendHeight, setLegendHeight] = useState(0);
  useLayoutEffect(() => {
    const bar = legendRowRef.current?.querySelector<HTMLElement>('[data-floating-legend]');
    if (!showsPlan || !bar) {
      setLegendHeight(0);
      return undefined;
    }
    const read = () => setLegendHeight(bar.getBoundingClientRect().height);
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(read);
    observer.observe(bar);
    return () => observer.disconnect();
  }, [showsPlan, layout]);
  const toastInset = showsPlan
    ? `calc(${GLASS_EDGE} + ${legendHeight}px + ${GLASS_GAP})`
    : GLASS_EDGE;

  /*
   * The plan's pieces, in the keyboard order Block A1 gives (the floors, then the right-hand panel,
   * then the legend's Show map and Recentre, then the plan). They float at their own layers, so the
   * order paints nothing differently.
   */
  const floorSelector = layout ? (
    <ThemeProvider theme={plannerTheme}>
      <FloorSlot
        data-testid="observers-floor-selector-slot"
        style={{ left: besideList, right: besidePanel }}
      >
        <FloorTabs
          look="floating"
          counts={floors.map(f => f.attention)}
          countLabel={attentionCountLabel}
        />
      </FloorSlot>
    </ThemeProvider>
  ) : null;
  const legendRow = layout ? (
    <ThemeProvider theme={plannerTheme}>
      <LegendRow ref={legendRowRef} style={{ left: besideList, right: besidePanel }}>
        <PlanLegendBar
          data-testid="observers-legend"
          style={LEGEND_IN_ROW}
          showMap={{ checked: showMap, onChange: onShowMapChange }}
        >
          <PlanLegend
            lead={legendLead}
            entries={[
              {
                key: 'unobserved',
                node: <Dot color={legendDot('unobserved')} label="No observer" />,
              },
              {
                key: 'check',
                node: (
                  <Dot
                    color={legendDot('check')}
                    label={
                      modelFailed ? "Can't check for moves right now" : 'Observer needs checking'
                    }
                  />
                ),
              },
              {
                key: 'added',
                node: (
                  <LegendAdded>
                    <Dot color={legendDot('added-unobserved')} label="Added in this draft" />
                    <Icon className="added-glyph" name={CHANGE_GLYPH.added} size="xs" />
                  </LegendAdded>
                ),
              },
              { key: 'picked', node: <LegendRing>Picked or hovered</LegendRing> },
            ]}
          />
        </PlanLegendBar>
        <Dock>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            aria-label="Recentre plan"
            iconLeft={<Icon name="crosshairs" />}
            onClick={onRecentre}
          >
            Recentre <Kbd keys="R" />
          </Button>
          <span className="divider" aria-hidden="true" />
          <ZoomPercent zoom={zoomStore} />
        </Dock>
      </LegendRow>
    </ThemeProvider>
  ) : null;
  const planBody = layout ? (
    <>
      <CanvasHost ref={canvasHostRef}>{planCanvas}</CanvasHost>
      <HoverOverlay store={hoverStore} describe={describeRef} host={canvasHostRef} />
    </>
  ) : null;

  /** The suggestions, or every camera, as a grid — with the feed read's three honest outcomes. */
  const grid = (mode: 'suggestions' | 'all-cameras') => {
    if (streamsStatus === 'failed') {
      return (
        <Banner surface="on-glass" tone="error" title={FEEDS_UNREADABLE_TITLE} role="alert">
          {FEEDS_UNREADABLE_BODY}
        </Banner>
      );
    }
    if (streamsStatus === 'loading') {
      return (
        <>
          <Note role="status">{FEEDS_LOADING}</Note>
          <Grid aria-hidden="true">
            {[0, 1, 2, 3].map(i => (
              <li key={i}>
                <Skeleton shape="media" />
              </li>
            ))}
          </Grid>
        </>
      );
    }
    if (feeds.length === 0) {
      return (
        <>
          <Note role="status">{NO_FEEDS_BODY}</Note>
          {onGoToCameraFeeds && (
            <Button type="button" variant="frosted" size="xs" onClick={onGoToCameraFeeds}>
              Go to Camera Feeds
            </Button>
          )}
        </>
      );
    }
    const byId = new Map(feeds.map(f => [Number(f.id), f]));
    const ranked =
      mode === 'suggestions'
        ? scores.slice(0, SHORTLIST_SIZE).map((s, i) => ({
            feed: byId.get(s.id),
            caption: suggestionCaption(s, i === 0),
            best: i === 0,
          }))
        : [...feeds]
            // Every camera by its name, as the header says (FR23).
            .sort((a, b) => NAME_COLLATOR.compare(a.name, b.name))
            .map(f => ({ feed: f, caption: null as string | null, best: false }));
    return (
      <>
        {mode === 'all-cameras' && scores.length === 0 && (
          <Note>No camera is placed on the plan, so every camera is listed by name.</Note>
        )}
        <Grid aria-label={mode === 'suggestions' ? 'Cameras likely to see it' : 'Every camera'}>
          {ranked.map(({ feed, caption, best }) =>
            feed ? (
              <GridTile
                key={feed.id}
                feed={feed}
                caption={caption}
                best={best}
                onPick={pick}
                onRefresh={onRefreshFeed}
                zoomRegions={zoomRegionsByFeed?.get(Number(feed.id))}
              />
            ) : null,
          )}
        </Grid>
      </>
    );
  };

  // The camera view has no head (its toolbar carries the camera, its picture time and Plan).
  const headerButtons =
    view.view === 'suggestions' && streamsStatus === 'ready' ? (
      <Button
        type="button"
        variant="frosted"
        size="xs"
        iconLeft={<Icon name="table-cells" />}
        onClick={onShowAllCameras}
      >
        All Cameras ({feeds.length})
      </Button>
    ) : view.view === 'all-cameras' && scores.length > 0 ? (
      <Button type="button" variant="frosted" size="xs" onClick={onShowSuggestions}>
        Likely to see it
      </Button>
    ) : view.view === 'side-by-side' ? (
      <Button
        type="button"
        variant="frosted"
        size="xs"
        iconLeft={<Icon name="plus" />}
        onClick={onAddCamera}
      >
        Add Camera
      </Button>
    ) : null;

  /*
   * The likely cameras' panel (R3): the picked item's head, today's grid content in its body, and
   * Draw Observer in its footer. Draw Observer opens the first-ranked camera with a region armed
   * (R3.6); with no ranked camera (feeds loading, failed or absent, the every-camera list) or while
   * linking regions to a camera, it is disabled and says why.
   */
  const topScore = view.view === 'suggestions' && streamsStatus === 'ready' ? scores[0] : undefined;
  const topFeed = topScore ? feeds.find(f => Number(f.id) === topScore.id) : undefined;
  /** The panel's view, narrowed: the likely cameras or every camera, else null. */
  const panelMode = view.view === 'suggestions' || view.view === 'all-cameras' ? view.view : null;
  const panelGrid = view.view === 'suggestions' || view.view === 'all-cameras' ? view : null;
  const linking = panelGrid?.linking === true;
  const adding = panelGrid?.adding === true;
  const captionId = useId();
  const drawReady = !!topFeed && !linking && !!onDrawObserver;
  const drawDescId = useId();
  // Linking, Draw Observer is described by the panel's caption, which says what to do instead.
  const drawDescription = !entity
    ? ''
    : drawReady
      ? `Opens ${topFeed?.name} ready to draw ${entity.label}`
      : 'No camera to suggest yet';
  const panelLabel =
    view.view === 'all-cameras'
      ? 'Every camera'
      : `Cameras most likely to see ${entity?.label ?? 'it'}`;
  const entityLine = entity
    ? [
        entity.code,
        entity.kind === 'floor' ? 'Floor' : entity.kind === 'surface' ? 'Zone' : 'Equipment',
        entity.observerCount === 0
          ? 'No observer'
          : `${entity.observerCount} observer${entity.observerCount === 1 ? '' : 's'}`,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  const stageRootRef = useRef<HTMLDivElement | null>(null);
  /*
   * Where the panel returns focus as it closes, read at that moment: this render's view. Onto a
   * camera or side by side, the stage root (STAGE_ROOT_ATTRIBUTE); otherwise null, so the panel's
   * own default, the row that opened it, takes focus back.
   */
  const panelReturnRef = useRef<HTMLElement | null>(null);
  panelReturnRef.current = onCard ? stageRootRef.current : null;
  const camerasPanel =
    panelView && entity ? (
      <PanelSlot
        // Keyed by the item: picking another remounts the card, so it records its own opener and
        // closing returns focus to that item's row, not the first pick's (DockedCard's opener).
        key={entity.refid}
        role="region"
        aria-label={panelLabel}
        style={{ width: INSPECTOR_WIDTH }}
        data-testid="observers-cameras-panel"
        data-control=""
      >
        <DockedCardFloating grip={null}>
          <DockedCard
            placement="narrow"
            icon={<KindGlyph kind={entity.kind} />}
            title={entity.label}
            headingLevel={3}
            sub={entityLine}
            onClose={onClosePanel}
            returnFocusTo={panelReturnRef}
            footer={
              <PanelFoot>
                <Button
                  type="button"
                  variant="primary"
                  iconLeft={<Icon name="pen" />}
                  disabled={!drawReady}
                  aria-describedby={linking ? captionId : drawDescId}
                  onClick={() => topScore && onDrawObserver?.(topScore.id)}
                >
                  Draw Observer
                </Button>
                {!linking && (
                  <span id={drawDescId} style={visuallyHidden}>
                    {drawDescription}
                  </span>
                )}
              </PanelFoot>
            }
          >
            <PanelCaption>
              <span className="caption" id={captionId}>
                {/* Adding or linking, the caption is the instruction the stage head gave (R3.3). */}
                {linking || adding
                  ? header.sub
                  : view.view === 'all-cameras'
                    ? 'Every camera, by name'
                    : 'Cameras most likely to see it'}
              </span>
              {headerButtons}
            </PanelCaption>
            {panelMode && grid(panelMode)}
            {/* Not while linking: a pick there links the regions, it opens nothing to draw on. */}
            {view.view === 'suggestions' &&
              !linking &&
              streamsStatus === 'ready' &&
              scores.length > 0 && (
                <PanelHint>
                  <Icon name="lightbulb" className="bulb" />
                  Pick a camera to draw {entity.label} on its picture.
                </PanelHint>
              )}
          </DockedCard>
        </DockedCardFloating>
      </PanelSlot>
    ) : null;

  /*
   * On the camera view the corner plan sits in the picture's own top-right corner, as v3 draws it.
   * The camera view leaves an empty slot for it (`data-camera-corner`), and it is portalled into its
   * slot once it exists. On side by side it stays in the card's body.
   */
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [cornerSlot, setCornerSlot] = useState<HTMLElement | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs after every commit: the camera slot exists only once the view has rendered; setCornerSlot bails out when unchanged
  useLayoutEffect(() => {
    const corner = onCamera
      ? (shellRef.current?.querySelector<HTMLElement>('[data-camera-corner]') ?? null)
      : null;
    setCornerSlot(prev => (prev === corner ? prev : corner));
  });
  const placeOnCamera = (node: React.ReactElement, slot: HTMLElement | null) =>
    onCamera ? (slot ? createPortal(node, slot) : null) : node;
  const cornerButtonId = useId();

  return (
    <StageRoot
      ref={stageRootRef}
      className={className}
      role="region"
      aria-label="Stage"
      tabIndex={-1}
      {...{ [STAGE_ROOT_ATTRIBUTE]: '' }}
    >
      {/* The stage's ONE polite region, on every view (Block A4). */}
      <StatusText role="status" data-stage-status="">
        {announced}
      </StatusText>
      {showsPlan && floorSelector}
      {camerasPanel}
      {showsPlan && legendRow}
      {showsPlan && <PlanLayer>{planBody}</PlanLayer>}
      {onCard && (
        <Shell
          ref={shellRef}
          surface="on-scene"
          data-testid="observers-stage-card"
          style={{
            left: `calc(${GLASS_START} + ${listOpen ? LIST_PANEL_WIDTH : LIST_PANEL_FOLDED_WIDTH} + ${GLASS_GAP})`,
          }}
          {...{ [OBSERVER_STAGE_ATTRIBUTE]: '' }}
        >
          {/*
           * On the camera view the head is gone (`observer-autogen-review-layout`, L3.1): the camera's
           * name, tone, picture time and Plan ride the camera's own toolbar, so the picture gets the
           * height.
           */}
          {!onCamera && (
            <DockedCardHead
              tone="none"
              icon={<Icon name={header.icon} />}
              title={header.title}
              headingLevel={3}
              sub={header.sub}
              headerActions={headerButtons}
            />
          )}
          <Body>
            {view.view === 'side-by-side' && <GridScroll>{sideBySide}</GridScroll>}
            {view.view === 'camera' && <CameraBody>{camera}</CameraBody>}
            {onCard &&
              layout &&
              placeOnCamera(
                <CornerPlan
                  className={onCamera ? 'on-camera' : undefined}
                  ref={cornerHostRef}
                  // A press or a key on the plan is the plan's, never the picture's (`observers-page-motion`
                  // FR8): its grip makes it a keyboard stop over the canvas.
                  data-control=""
                  role="region"
                  aria-labelledby={cornerButtonId}
                >
                  <ThemeProvider theme={plannerTheme}>
                    {/* Only the corner draws pictures: the stage plan keeps its live map. */}
                    <CornerRenderer renderer={cornerRenderer}>
                      <ReadOnlyCanvas
                        layout={layout}
                        floorIndex={floorIndex}
                        driverName="observers-corner"
                        // On the camera view it zooms and pans like the full-size plan, to look around
                        // the floor while matching the camera (`observer-corner-plan-zoom-pan`); a passive
                        // picture on the other views. Its role stays `region`: the stage plan is the `img`.
                        interactive={onCamera}
                        // Resizing the card keeps the plan's zoom and centre, so a bigger card shows more
                        // floor (`observers-corner-plan-resize`, FR14).
                        keepViewOnResize={onCamera}
                        ariaLabel="Plan preview"
                        pickable={!!onPickFromCorner}
                        // On the camera view it frames the camera's coverage (the whole floor for a
                        // camera not on it) and keeps that frame across picks, sliding just enough to
                        // show each (`observer-corner-plan-camera-framing`, R1 to R5); elsewhere a
                        // passive picture can only follow the pick, so it fits 12 m round it (R7).
                        fitBounds={onCamera ? (coverage?.boxPx ?? null) : null}
                        focusId={onCamera ? null : (entity?.refid ?? null)}
                        revealId={onCamera ? (entity?.refid ?? null) : null}
                        fitPadding={CORNER_FIT_PADDING_PX}
                        markers={cornerMarkers}
                        machineColours={machineColours}
                        onSelectEntity={onPickFromCorner ? pickFromCorner : undefined}
                        onHoverEntity={onPickFromCorner ? onCornerHover : undefined}
                        onBackgroundClick={onPickFromCorner ? undefined : onPlanBack}
                      />
                    </CornerRenderer>
                  </ThemeProvider>
                  {cornerHover && (
                    <CornerHover hover={cornerHover} describe={describeRef} host={cornerHostRef} />
                  )}
                  <Button
                    id={cornerButtonId}
                    className="back"
                    type="button"
                    variant="frosted"
                    size="xs"
                    // On the camera view the header has its own *Plan*, so the corner's says which it is.
                    aria-label={onCamera ? CORNER_PLAN_LABEL : PLAN_BACK_LABEL}
                    iconLeft={<Icon name="map" />}
                    onClick={onPlanBack}
                  >
                    Plan <Kbd keys="P" />
                  </Button>
                </CornerPlan>,
                cornerSlot,
              )}
          </Body>
        </Shell>
      )}
      {/* One region on every view, centred at the foot of the window, clear of the legend (R6). */}
      <ToastRegion name={STAGE_TOAST_REGION} placement="bottom-centre" bottomInset={toastInset} />
    </StageRoot>
  );
};

export default ObserverStage;

/** The dashed *Add Camera* tile at the end of the side-by-side grid (FR26). */
export const AddCameraTile: React.FC<{ entityLabel: string; onAdd: () => void }> = ({
  entityLabel,
  onAdd,
}) => (
  <AddTile
    surface="on-glass"
    empty
    // `interactive` makes it a button: its role, tab stop, focus ring, and Enter and Space.
    interactive
    aria-label={`Add Camera for ${entityLabel}`}
    data-add-camera
    onClick={onAdd}
  >
    <Icon name="plus" size="lg" />
    <span className="add-title">Add Camera</span>
    <span className="add-sub">Suggests the cameras most likely to see {entityLabel}</span>
  </AddTile>
);
